import React, { FunctionComponent, useContext, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import {
  AdminApi,
  AdminOrganization,
  AdminOrganizationsListLoginTypeEnum,
  AdminOrganizationsListOrderingEnum,
  AdminOrganizationsValuesRetrieveFieldEnum,
  LoginTypeEnum,
} from 'src/api';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';

export interface OrganizationsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  organizations?: AdminOrganization[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission?: boolean;
  onDelete: (organization: AdminOrganization) => void;
  goToDetails: (organizationId: string) => void;
  setFilterPopupHeight?: (height: number) => void;
}

const OrganizationsTable: FunctionComponent<OrganizationsTableProps> = (props) => {
  const {
    className,
    loading,
    ordering,
    onOrderBy,
    hasPermission,
    onDelete,
    organizations,
    paginationProps,
    goToDetails,
    setFilterPopupHeight,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const { activeLocale } = useContext(I18NContext);

  const loginTypeFilterOptions = useMemo(() => {
    return Object.values(AdminOrganizationsListLoginTypeEnum).map((loginType) => ({
      id: loginType,
      name: translate(`studioOrganizationsTable.${snakeToCamelCaseWithNumbers(loginType)}Login`),
    }));
  }, [translate]);

  const organizationContractFilterOptions = useMemo(() => {
    return ['true', 'false'].map((active) => ({
      id: active,
      name: translate(`commonOptions.${active === 'true' ? 'true' : 'false'}`),
    }));
  }, [translate]);

  return (
    <div className={cx('organizations-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={organizations}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        onGoToDetails={goToDetails}
        setFilterPopupHeight={setFilterPopupHeight}
        cells={[
          {
            title: <Translate name="studioOrganizationsTable.organization" />,
            render: ({ name }) => name,
            orderingFieldName: AdminOrganizationsListOrderingEnum.NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'name',
            searchFieldName: AdminOrganizationsValuesRetrieveFieldEnum.NAME,
            getFilterData: (params: any) => adminApi.adminOrganizationsValuesRetrieve(params),
          },
          {
            title: <Translate name="studioOrganizationsTable.serviceContracts.banking" />,
            orderingFieldName: AdminOrganizationsListOrderingEnum.HAS_SC_BANKING_ASC,
            render: ({ hasScBanking }) => {
              return <Translate name={`commonOptions.${hasScBanking ? 'true' : 'false'}`} />;
            },
            filterName: 'hasScBanking',
            getFilterData: () => {
              return organizationContractFilterOptions;
            },
          },
          {
            title: <Translate name="studioOrganizationsTable.serviceContracts.core" />,
            orderingFieldName: AdminOrganizationsListOrderingEnum.HAS_SC_CORE_ASC,
            render: ({ hasScCore }) => {
              return <Translate name={`commonOptions.${hasScCore ? 'true' : 'false'}`} />;
            },
            filterName: 'hasScCore',
            getFilterData: () => {
              return organizationContractFilterOptions;
            },
          },
          {
            title: <Translate name="studioOrganizationsTable.serviceContracts.distributionHub" />,
            orderingFieldName: AdminOrganizationsListOrderingEnum.HAS_SC_DISTRIBUTION_HUB_ASC,
            render: ({ hasScDistributionHub }) => {
              return <Translate name={`commonOptions.${hasScDistributionHub ? 'true' : 'false'}`} />;
            },
            filterName: 'hasScDistributionHub',
            getFilterData: () => {
              return organizationContractFilterOptions;
            },
          },
          {
            title: <Translate name="studioOrganizationsTable.serviceContracts.launchpad" />,
            orderingFieldName: AdminOrganizationsListOrderingEnum.HAS_SC_LAUNCHPAD_ASC,
            render: ({ hasScLaunchpad }) => {
              return <Translate name={`commonOptions.${hasScLaunchpad ? 'true' : 'false'}`} />;
            },
            filterName: 'hasScLaunchpad',
            getFilterData: () => {
              return organizationContractFilterOptions;
            },
          },
          {
            title: <Translate name="studioOrganizationsTable.loginMethod" />,
            render: ({ loginType }) => (
              <Translate
                name={`${
                  loginType === LoginTypeEnum.GOOGLE_SSO
                    ? 'studioOrganizationsTable.googleSsoLogin'
                    : 'studioOrganizationsTable.passwordLogin'
                }`}
              />
            ),
            orderingFieldName: AdminOrganizationsListOrderingEnum.LOGIN_TYPE_ASC,
            type: CellTypeEnum.text,
            filterName: 'loginType',
            getFilterData: () => {
              return loginTypeFilterOptions;
            },
          },
          {
            title: <Translate name="studioOrganizationsTable.actions" />,
            render: (organization) => (
              <ActionButtons align="center" spacingTop="none">
                {organization.hasUserOrGroupAssigned ? (
                  <Tooltip
                    content={<Translate name="studioOrganizationsTable.hasUserOrGroupAssignedTooltip" />}
                    position={TooltipPosition.LEFT}
                  >
                    <Button variant="secondary" size="xsmall" disabled={true}>
                      <Translate name="common.delete" />
                    </Button>
                  </Tooltip>
                ) : (
                  <Button variant="secondary" onClick={() => onDelete(organization)} size="xsmall">
                    <Translate name="common.delete" />
                  </Button>
                )}
              </ActionButtons>
            ),
            alignContent: 'left',
            width: activeLocale === 'de' ? 112 : 100,
          },
        ]}
      />
    </div>
  );
};

export default OrganizationsTable;
